import { useState } from 'react'
import { isHTTPError } from 'ky'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { AlertTriangle, LogOut } from 'lucide-react'
import type { UserListItem, ForceLogoutResponse } from '@common/types'
import { forceLogoutUser } from '@frontend/lib/api'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@frontend/components/ui/dialog'
import { Button } from '@frontend/components/ui/button'
import { Spinner } from '@frontend/components/ui/spinner'

interface ForceLogoutModalProps {
  user: UserListItem | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ForceLogoutModal({ user, open, onOpenChange }: ForceLogoutModalProps) {
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)

  const logoutMutation = useMutation({
    mutationFn: () => forceLogoutUser(user!.userId),
    onSuccess: (data: ForceLogoutResponse) => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      onOpenChange(false)
      const count = data.sessionsTerminated
      toast.success(
        count === 0 ? 'User had no active sessions' : `Terminated ${count} session${count !== 1 ? 's' : ''}`
      )
    },
    onError: (err) => {
      if (isHTTPError(err)) {
        const data = err.data as { error?: string }
        setError(data.error || 'Failed to force logout')
      } else {
        setError('Failed to force logout')
      }
    },
  })

  const handleConfirm = () => {
    setError(null)
    logoutMutation.mutate()
  }

  const isSubmitting = logoutMutation.isPending

  if (!user) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Force Logout
          </DialogTitle>
          <DialogDescription>
            This will terminate all active sessions for <strong>{user.email}</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <p className="text-sm text-muted-foreground">
            The user will be immediately logged out of all devices and will need to sign in again. This action cannot be
            undone.
          </p>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-md text-sm">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            {error}
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleConfirm} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Spinner size="sm" className="mr-2" />
                Logging out...
              </>
            ) : (
              <>
                <LogOut className="h-4 w-4 mr-2" />
                Force Logout
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
